import { createContext } from 'preact';
import { useContext } from 'preact/hooks';
import type { VaultConfig } from '@hud/core';
import type { VaultStore } from './store.js';

/**
 * What every widget can reach without prop-drilling.
 *
 * The store holds the whole vault in memory, so widgets read synchronously and
 * write optimistically — no loading states, no async in render.
 */
export interface HudContextValue {
  store: VaultStore;
  /** Vault-wide defaults from `config.md`. Widgets read `timezone` from here. */
  config: VaultConfig;
  /**
   * Vault path → a URL the browser can load directly (images, video).
   *
   * Adapter-specific on purpose: the dev server streams bytes from disk, while
   * the GitHub adapter will have to mint an authenticated or data URL. Widgets
   * shouldn't know which.
   */
  assetUrl: (path: string) => string;
  /**
   * Async fallback for vaults whose blobs need credentials.
   *
   * A private GitHub repo's raw URLs 404 without an auth header, and an `<img>`
   * tag cannot send one — so the adapter fetches the bytes and hands back a
   * data URL instead. Set only when `assetUrl` alone won't load.
   */
  assetDataUrl?: (path: string) => Promise<string>;
  /** Open the raw markdown editor on a vault file. */
  openEditor: (path: string) => void;
}

export const HudContext = createContext<HudContextValue | null>(null);

export function useHud(): HudContextValue {
  const value = useContext(HudContext);
  if (!value) throw new Error('useHud must be used inside <HudContext.Provider>');
  return value;
}
